// frontend/src/components/common/LocaleGate.jsx
//
// Gate for the three-axis locale fields:
//   language     — BCP 47 language subtag (drives translations)
//   region_code  — ISO 3166-1 alpha-2 (drives Intl.* number/date formats)
//   timezone     — IANA name (drives timestamp display)
//
// Mounted inside AppLayout so it fires on every authenticated SaaS page. When
// any of `User.{language, region_code, timezone}` is empty, a non-dismissible
// popup asks for all three, prefilled with the user's effective_* values
// (which fall back to Store server-side). On submit, PATCHes
// /auth/user-profile/me/, refreshes the auth context, and closes.
//
// Why a gate:
//   - Sign-in and invites seed the columns from a store
//     (backend authentication.locale_seed), but only where the store has a
//     value; the gate asks every user whose columns are still empty.
//   - SetupAccount.jsx hands off to login after password is set, so
//     post-login is the first reliable place to ask. Gating here means
//     no path through the SaaS app can bypass the form.
//
// Non-dismissible: no close button, no escape-to-cancel, no backdrop
// click. The form is the only exit. No "Use store default" entry either:
// this form is for explicit choices; the defer-to-store option lives in
// /user-profile only.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DialogErrorBanner from './DialogErrorBanner';
import { useAuth } from '../../contexts/AuthContext';
import { translateRequestError } from '../../queries/api';
import { sessionService } from '../../services/SessionApiService';
import {
  COMMON_TIMEZONES,
  languageOptions,
  regionOptions,
} from '../../utils/localeOptions';

// Whether the gate's popup is up for this user: ANY of the three axes is
// unset. The raw fields (not effective_*) are the source of truth —
// effective_* can be non-empty thanks to Store fallback even when the user
// has never made an explicit choice.
//
// user==null is treated as "not loaded yet, suppress" rather than
// "any axis empty, fire" — AppLayout already guards against rendering
// children before userContext loads, but the explicit guard here means
// the gate never flashes an empty Dialog if the auth context briefly
// emits null during a refresh.
export function needsLocaleGate(user) {
  if (!user) return false;
  return !user.language || !user.region_code || !user.timezone;
}

export default function LocaleGate() {
  const { user } = useAuth();
  if (!needsLocaleGate(user)) return null;

  return <LocaleGateDialog user={user} />;
}

function LocaleGateDialog({ user }) {
  const translation = useTranslation();
  const { t } = translation;
  const { refreshUser } = useAuth();
  const [language, setLanguage] = useState(user.language || user.effective_language || '');
  const [region, setRegion] = useState(user.region_code || user.effective_region_code || '');
  const [timezone, setTimezone] = useState(user.timezone || user.effective_timezone || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // All three required: the form exists to finish the locale axes, and a
  // partial submit would bounce off this gate on the next load.
  const isValid = !!(language && region && timezone);

  const handleTimezoneChange = (_event, value) => {
    setTimezone(value || '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await sessionService.updateUserProfile({ language, region_code: region, timezone });
      // Refresh auth context so the gate's predicate flips to false and
      // the rest of the app sees the new locale immediately.
      await refreshUser();
    } catch (err) {
      console.error('LocaleGate submit failed:', err?.message || err);
      // A refused axis answers with its own code (timezone_invalid,
      // region_code_invalid, language_invalid).
      setError(translateRequestError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  const languages = languageOptions(translation);
  const regions = regionOptions(translation, { current: region });

  return (
    <Dialog
      open={true}
      className="dialog-form"
      // The paper is the form, so the Continue button in the button bar
      // submits it.
      slotProps={{ paper: { component: 'form', onSubmit: handleSubmit } }}
      // Non-dismissible: no close on backdrop click or Escape. The form
      // submit is the only way out.
      onClose={() => { /* no-op — non-dismissible */ }}
    >
      <DialogTitle>{t('localeGate.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {t('localeGate.intro')}
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label={t('userProfile.language')}
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            size="small"
            fullWidth
            required
          >
            {languages.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label={t('userProfile.region')}
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            size="small"
            fullWidth
            required
            helperText={t('userProfile.regionDrivesFormatsHelp')}
          >
            {regions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Autocomplete
          value={timezone || null}
          onChange={handleTimezoneChange}
          onInputChange={handleTimezoneChange}
          options={COMMON_TIMEZONES}
          freeSolo
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('userProfile.timezone')}
              required
            />
          )}
        />

        <Typography variant="caption" color="text.secondary">
          {t('localeGate.footerNote')}
        </Typography>
        <DialogErrorBanner error={error} />
      </DialogContent>
      <DialogActions>
        <Button
          type="submit"
          variant="contained"
          disabled={!isValid}
          loading={submitting}
        >
          {t('localeGate.continue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
